import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { RapportoIntervento, RapportoInterventoForm, Commessa, Sottocommessa, TipoAllegato, OperaioProfile } from '../models/types';
import { rapportiService, commesseService, sottocommesseService, tipiAllegatoService } from '../services/rapportiService';
import SearchableSelect, { SelectOption } from '../components/SearchableSelect/SearchableSelect';
import './HomePage.css';

// Icone SVG inline
const IconPlus = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const IconClock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <polyline points="12,6 12,12 16,14"></polyline>
  </svg>
);

const IconCalendar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const IconEdit = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const IconTrash = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3,6 5,6 21,6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const IconFileText = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <polyline points="14,2 14,8 20,8"></polyline>
    <line x1="16" y1="13" x2="8" y2="13"></line>
    <line x1="16" y1="17" x2="8" y2="17"></line>
    <polyline points="10,9 9,9 8,9"></polyline>
  </svg>
);

const IconCheck = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20,6 9,17 4,12"></polyline>
  </svg>
);

const IconCamera = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
    <circle cx="12" cy="13" r="4"></circle>
  </svg>
);

const IconImage = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <circle cx="8.5" cy="8.5" r="1.5"></circle>
    <polyline points="21,15 16,10 5,21"></polyline>
  </svg>
);

const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const IconPaperclip = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
  </svg>
);

const IconAlertTriangle = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

const HomePage: React.FC = () => {
  const { user } = useAuth();

  // Ottieni gli operai associati all'utente
  const operai = useMemo<OperaioProfile[]>(() => {
    return user?.operai || (user?.operaio ? [user.operaio] : []);
  }, [user?.operai, user?.operaio]);

  // Verifica se l'utente pu� inserire rapporti
  const puoInserireRapporti = operai.length > 0;
  const operaioSingolo = operai.length === 1;

  // State per i rapporti
  const [rapporti, setRapporti] = useState<RapportoIntervento[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // State per il form - l'operaio viene preselezionato se ce n'� uno solo
  const getInitialFormData = useCallback((): RapportoInterventoForm => {
    const operaioDefault = operaioSingolo ? operai[0] : null;
    return {
      operaioId: operaioDefault?.id || 0,
      operaioNome: operaioDefault?.nome || '',
      data: new Date().toISOString().split('T')[0],
      ore: 0,
      descrizione: '',
      commessaId: 0,
      commessaTitolo: '',
      sottocommessaId: 0,
      sottocommessaNome: '',
    };
  }, [operai, operaioSingolo]);

  const [formData, setFormData] = useState<RapportoInterventoForm>(getInitialFormData);

  // State per le select con API
  const [commesse, setCommesse] = useState<Commessa[]>([]);
  const [sottocommesse, setSottocommesse] = useState<Sottocommessa[]>([]);
  const [loadingCommesse, setLoadingCommesse] = useState(false);
  const [loadingSottocommesse, setLoadingSottocommesse] = useState(false);

  // State per allegati
  const [showAllegatiModal, setShowAllegatiModal] = useState(false);
  const [selectedRapportoId, setSelectedRapportoId] = useState<string | null>(null);
  const [tipiAllegato, setTipiAllegato] = useState<TipoAllegato[]>([]);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [newAllegatoDescrizione, setNewAllegatoDescrizione] = useState('');
  const [newAllegatoTipoId, setNewAllegatoTipoId] = useState<number>(0);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  
  // Refs per input file
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  // Carica i rapporti e i tipi allegato all'avvio
  useEffect(() => {
    loadRapporti();
    setTipiAllegato(tipiAllegatoService.getAll());

    // Ascolta l'evento di sincronizzazione per ricaricare i rapporti
    const handleRapportiSync = () => {
      loadRapporti();
    };

    window.addEventListener('rapporti-sync', handleRapportiSync);
    
    return () => {
      window.removeEventListener('rapporti-sync', handleRapportiSync);
    };
  }, []);

  // Aggiorna il form quando cambiano gli operai
  useEffect(() => {
    setFormData(getInitialFormData());
  }, [getInitialFormData]);

  // Carica le sottocommesse quando cambia la commessa selezionata
  useEffect(() => {
    if (formData.commessaId > 0) {
      loadSottocommesse(formData.commessaId);
    } else {
      setSottocommesse([]);
    }
  }, [formData.commessaId]);

  const loadRapporti = () => {
    setRapporti(rapportiService.getAll());
  };

  // Funzione per caricare le commesse aperte con ricerca
  const loadCommesse = useCallback(async (search?: string) => {
    setLoadingCommesse(true);
    try {
      const data = await commesseService.getCommesseAperte(search);
      setCommesse(data);
    } catch (error) {
      console.error('Errore caricamento commesse:', error);
    } finally {
      setLoadingCommesse(false);
    }
  }, []);

  // Funzione per caricare le sottocommesse con ricerca
  const loadSottocommesse = useCallback(async (commessaId: number, search?: string) => {
    if (commessaId <= 0) {
      setSottocommesse([]);
      return;
    }
    setLoadingSottocommesse(true);
    try {
      const data = await sottocommesseService.getByCommessa(commessaId, search);
      setSottocommesse(data);
    } catch (error) {
      console.error('Errore caricamento sottocommesse:', error);
    } finally {
      setLoadingSottocommesse(false);
    }
  }, []);

  // Converti commesse in opzioni per SearchableSelect
  const commesseOptions: SelectOption[] = commesse.map(c => ({
    value: c.id,
    label: `${c.numero}/${c.anno} - ${c.titolo || ''}`,
    subLabel: c.anagraficaNome || c.impiantoNome,
  }));

  // Converti sottocommesse in opzioni per SearchableSelect
  const sottocommesseOptions: SelectOption[] = sottocommesse.map(s => ({
    value: s.id,
    label: `${s.codice} - ${s.nome}`,
    subLabel: s.descrizione,
  }));

  // Handler per ricerca commesse
  const handleSearchCommesse = useCallback((searchText: string) => {
    loadCommesse(searchText || undefined);
  }, [loadCommesse]);

  // Handler per ricerca sottocommesse
  const handleSearchSottocommesse = useCallback((searchText: string) => {
    if (formData.commessaId > 0) {
      loadSottocommesse(formData.commessaId, searchText || undefined);
    }
  }, [formData.commessaId, loadSottocommesse]);

  // Handler per selezione commessa
  const handleCommessaChange = useCallback((value: number, option: SelectOption | undefined) => {
    const commessa = commesse.find(c => c.id === value);
    setFormData(prev => ({
      ...prev,
      commessaId: value,
      commessaTitolo: commessa?.titolo || option?.label || '',
      sottocommessaId: 0,
      sottocommessaNome: '',
    }));
  }, [commesse]);

  // Handler per selezione sottocommessa
  const handleSottocommessaChange = useCallback((value: number, option: SelectOption | undefined) => {
    const sottocommessa = sottocommesse.find(s => s.id === value);
    setFormData(prev => ({
      ...prev,
      sottocommessaId: value,
      sottocommessaNome: sottocommessa?.nome || option?.label || '',
    }));
  }, [sottocommesse]);

  // Handler per selezione operaio
  const handleOperaioChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const operaioId = parseInt(e.target.value);
    const operaio = operai.find(o => o.id === operaioId);
    setFormData(prev => ({
      ...prev,
      operaioId: operaioId,
      operaioNome: operaio?.nome || '',
    }));
  }, [operai]);

  const getFirstName = (name: string | undefined): string => {
    if (!name) return '';
    return name.split(' ')[0];
  };

  const resetForm = useCallback(() => {
    setFormData(getInitialFormData());
    setEditingId(null);
    setCommesse([]);
    setSottocommesse([]);
  }, [getInitialFormData]);

  const handleOpenForm = () => {
    if (!puoInserireRapporti) {
      return; // Non dovrebbe mai arrivare qui, ma per sicurezza
    }
    resetForm();
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'ore') {
      setFormData(prev => ({
        ...prev,
        ore: parseFloat(value) || 0,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.operaioId) {
      alert('Seleziona un operaio');
      return;
    }

    if (!formData.commessaId || !formData.sottocommessaId || formData.ore <= 0) {
      alert('Compila tutti i campi obbligatori');
      return;
    }

    // Controllo data non nel futuro
    const dataRapporto = new Date(formData.data);
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    dataRapporto.setHours(0, 0, 0, 0);
    
    if (dataRapporto > oggi) {
      alert("Non &egrave; possibile inserire rapporti con data futura");
      return;
    }

    if (editingId) {
      rapportiService.update(editingId, formData);
    } else {
      rapportiService.create(formData);
    }

    loadRapporti();
    handleCloseForm();
  };

  const handleEdit = async (rapporto: RapportoIntervento) => {
    // Pre-carica commesse e sottocommesse per la modifica
    setLoadingCommesse(true);
    try {
      const [commesseData, sottocommesseData] = await Promise.all([
        commesseService.getCommesseAperte(),
        sottocommesseService.getByCommessa(rapporto.commessaId)
      ]);
      setCommesse(commesseData);
      setSottocommesse(sottocommesseData);
    } catch (error) {
      console.error('Errore caricamento dati per modifica:', error);
    } finally {
      setLoadingCommesse(false);
    }

    setFormData({
      operaioId: rapporto.operaioId,
      operaioNome: rapporto.operaioNome,
      data: rapporto.data,
      ore: rapporto.ore,
      descrizione: rapporto.descrizione,
      commessaId: rapporto.commessaId,
      commessaTitolo: rapporto.commessaTitolo,
      sottocommessaId: rapporto.sottocommessaId,
      sottocommessaNome: rapporto.sottocommessaNome,
    });
    setEditingId(rapporto.id);
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Eliminare questo rapporto?')) {
      rapportiService.delete(id);
      loadRapporti();
    }
  };

  const handleToggleStato = (id: string) => {
    rapportiService.toggleStato(id);
    loadRapporti();
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' });
  };

  // ===== Gestione Allegati =====
  
  const handleOpenAllegati = (rapportoId: string) => {
    setSelectedRapportoId(rapportoId);
    setShowAllegatiModal(true);
    resetAllegatoForm();
  };

  const handleCloseAllegati = () => {
    setShowAllegatiModal(false);
    setSelectedRapportoId(null);
    resetAllegatoForm();
  };

  const resetAllegatoForm = () => {
    setShowPhotoOptions(false);
    setPreviewImage(null);
    setNewAllegatoDescrizione('');
    setNewAllegatoTipoId(tipiAllegato[0]?.id || 0);
  };

  const handleTakePhoto = () => {
    cameraInputRef.current?.click();
  };

  const handleSelectFromGallery = () => {
    galleryInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Leggi il file come base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUri = event.target?.result as string;
      setPreviewImage(dataUri);
      setShowPhotoOptions(true);
    };
    reader.readAsDataURL(file);

    // Reset input per permettere di selezionare lo stesso file
    e.target.value = '';
  };

  const handleSaveAllegato = () => {
    if (!selectedRapportoId || !previewImage) return;

    const tipoAllegato = tipiAllegato.find(t => t.id === newAllegatoTipoId);
    
    rapportiService.addAllegato(selectedRapportoId, {
      dataUri: previewImage,
      descrizione: newAllegatoDescrizione,
      tipoAllegatoId: newAllegatoTipoId,
      tipoAllegatoNome: tipoAllegato?.nome || '',
    });

    loadRapporti();
    resetAllegatoForm();
  };

  const handleDeleteAllegato = (rapportoId: string, allegatoId: string) => {
    if (window.confirm('Eliminare questa foto?')) {
      rapportiService.removeAllegato(rapportoId, allegatoId);
      loadRapporti();
    }
  };

  const getSelectedRapporto = (): RapportoIntervento | undefined => {
    if (!selectedRapportoId) return undefined;
    return rapporti.find(r => r.id === selectedRapportoId);
  };

  // Se non ci sono operai associati, mostra un messaggio di avviso
  if (!puoInserireRapporti) {
    return (
      <div className="home-page">
        {/* Welcome Section */}
        <section className="welcome-section">
          <h1 className="welcome-title">
            Ciao, {getFirstName(user?.nominativo)}!
          </h1>
        </section>

        {/* Messaggio di avviso: nessun operaio associato */}
        <section className="no-operai-section">
          <div className="no-operai-card">
            <div className="no-operai-icon">
              <IconAlertTriangle />
            </div>
            <h2>Nessun operaio associato</h2>
            <p>
              Il tuo account non ha operai associati.<br />
              Per poter inserire rapporti ore, contatta l'amministratore per associare almeno un operaio al tuo account.
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelected}
        style={{ display: 'none' }}
      />

      {/* Welcome Section */}
      <section className="welcome-section">
        <h1 className="welcome-title">
          Ciao, {getFirstName(user?.nominativo)}!
        </h1>
        <p className="welcome-subtitle">
          Registra le tue attivit&agrave;
        </p>
      </section>

      {/* Add Button */}
      <button className="fab-button" onClick={handleOpenForm} title="Nuovo rapporto">
        <IconPlus />
      </button>

      {/* Rapporti List */}
      <section className="rapporti-section">
        <h2 className="section-title">
          <IconFileText />
          <span>I tuoi rapporti</span>
        </h2>

        {rapporti.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <IconClock />
            </div>
            <p>Nessun rapporto registrato</p>
            <button className="btn-primary" onClick={handleOpenForm}>
              + Aggiungi il primo
            </button>
          </div>
        ) : (
          <div className="rapporti-list">
            {rapporti.map(rapporto => (
              <div key={rapporto.id} className="rapporto-card">
                <div className="rapporto-header">
                  <div className="rapporto-date">
                    <IconCalendar />
                    <span>{formatDate(rapporto.data)}</span>
                  </div>
                  <button
                    className={`badge badge-${rapporto.stato}`}
                    onClick={() => handleToggleStato(rapporto.id)}
                    title="Clicca per cambiare stato"
                  >
                    {rapporto.stato === 'pronto' && <IconCheck />}
                    <span>{rapporto.stato === 'draft' ? 'Bozza' : 'Pronto'}</span>
                  </button>
                </div>

                <div className="rapporto-body">
                  <div className="rapporto-commessa">
                    <strong>{rapporto.commessaTitolo}</strong>
                    <span className="sottocommessa">{rapporto.sottocommessaNome}</span>
                  </div>
                  
                  <div className="rapporto-ore">
                    <IconClock />
                    <span className="ore-value">{rapporto.ore}h</span>
                  </div>
                </div>

                {/* Mostra operaio se ci sono pi� operai associati */}
                {!operaioSingolo && (
                  <div className="rapporto-operaio">
                    <span className="operaio-label">Operaio:</span>
                    <span className="operaio-nome">{rapporto.operaioNome}</span>
                  </div>
                )}

                {rapporto.descrizione && (
                  <p className="rapporto-descrizione">{rapporto.descrizione}</p>
                )}

                {/* Allegati preview */}
                {rapporto.allegati && rapporto.allegati.length > 0 && (
                  <div className="rapporto-allegati-preview">
                    <IconPaperclip />
                    <span>{rapporto.allegati.length} foto</span>
                    <div className="allegati-thumbnails">
                      {rapporto.allegati.slice(0, 3).map(allegato => (
                        <img 
                          key={allegato.id} 
                          src={allegato.dataUri} 
                          alt={allegato.descrizione || 'Foto'} 
                          className="allegato-thumb"
                        />
                      ))}
                      {rapporto.allegati.length > 3 && (
                        <span className="allegati-more">+{rapporto.allegati.length - 3}</span>
                      )}
                    </div>
                  </div>
                )}

                <div className="rapporto-actions">
                  <button 
                    className="btn-icon" 
                    onClick={() => handleOpenAllegati(rapporto.id)}
                    title="Gestisci foto"
                  >
                    <IconCamera />
                  </button>
                  <button 
                    className="btn-icon" 
                    onClick={() => handleEdit(rapporto)}
                    title="Modifica"
                  >
                    <IconEdit />
                  </button>
                  <button 
                    className="btn-icon btn-danger" 
                    onClick={() => handleDelete(rapporto.id)}
                    title="Elimina"
                  >
                    <IconTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Modal Form Rapporto */}
      {showForm && (
        <div className="modal-overlay" onClick={handleCloseForm}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 className="modal-title">
              {editingId ? 'Modifica Rapporto' : 'Nuovo Rapporto'}
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Campo Operaio: select se multipli, readonly se singolo */}
              <div className="form-group">
                <label htmlFor="operaioId">Operaio *</label>
                {operaioSingolo ? (
                  <input
                    type="text"
                    id="operaioNome"
                    value={formData.operaioNome}
                    readOnly
                    className="input-readonly"
                  />
                ) : (
                  <select
                    id="operaioId"
                    name="operaioId"
                    value={formData.operaioId || ''}
                    onChange={handleOperaioChange}
                    required
                  >
                    <option value="">Seleziona operaio...</option>
                    {operai.map(op => (
                      <option key={op.id} value={op.id}>
                        {op.nome}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="data">Data</label>
                  <input
                    type="date"
                    id="data"
                    name="data"
                    value={formData.data}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ore">Ore</label>
                  <input
                    type="number"
                    id="ore"
                    name="ore"
                    value={formData.ore || ''}
                    onChange={handleInputChange}
                    min="0.5"
                    max="24"
                    step="0.5"
                    required
                    placeholder="0"
                  />
                </div>
              </div>

              <SearchableSelect
                label="Commessa"
                required
                options={commesseOptions}
                value={formData.commessaId || null}
                onChange={handleCommessaChange}
                onSearch={handleSearchCommesse}
                loading={loadingCommesse}
                placeholder="Cerca e seleziona commessa..."
              />

              <SearchableSelect
                label="Sottocommessa"
                required
                options={sottocommesseOptions}
                value={formData.sottocommessaId || null}
                onChange={handleSottocommessaChange}
                onSearch={handleSearchSottocommesse}
                loading={loadingSottocommesse}
                disabled={!formData.commessaId}
                placeholder={formData.commessaId ? "Cerca e seleziona sottocommessa..." : "Seleziona prima una commessa"}
              />

              <div className="form-group">
                <label htmlFor="descrizione">Descrizione</label>
                <textarea
                  id="descrizione"
                  name="descrizione"
                  value={formData.descrizione}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Descrizione dell'intervento..."
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={handleCloseForm}>
                  Annulla
                </button>
                <button type="submit" className="btn-primary">
                  {editingId ? 'Salva modifiche' : 'Aggiungi'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Allegati */}
      {showAllegatiModal && selectedRapportoId && (
        <div className="modal-overlay" onClick={handleCloseAllegati}>
          <div className="modal-content modal-allegati" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Foto allegate</h2>
              <button className="btn-close" onClick={handleCloseAllegati}>
                <IconX />
              </button>
            </div>

            {/* Preview nuova foto */}
            {previewImage ? (
              <div className="new-allegato-form">
                <div className="preview-container">
                  <img src={previewImage} alt="Anteprima" className="preview-image" />
                  <button 
                    className="btn-remove-preview" 
                    onClick={() => setPreviewImage(null)}
                  >
                    <IconX />
                  </button>
                </div>

                <div className="form-group">
                  <label htmlFor="tipoAllegato">Tipologia *</label>
                  <select
                    id="tipoAllegato"
                    value={newAllegatoTipoId}
                    onChange={(e) => setNewAllegatoTipoId(parseInt(e.target.value))}
                  >
                    {tipiAllegato.map(tipo => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="allegatoDescrizione">Descrizione</label>
                  <input
                    type="text"
                    id="allegatoDescrizione"
                    value={newAllegatoDescrizione}
                    onChange={(e) => setNewAllegatoDescrizione(e.target.value)}
                    placeholder="Descrizione della foto..."
                  />
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={() => setPreviewImage(null)}
                  >
                    Annulla
                  </button>
                  <button 
                    type="button" 
                    className="btn-primary" 
                    onClick={handleSaveAllegato}
                  >
                    Salva foto
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Bottoni per aggiungere foto */}
                <div className="add-photo-buttons">
                  <button className="btn-photo" onClick={handleTakePhoto}>
                    <IconCamera />
                    <span>Scatta foto</span>
                  </button>
                  <button className="btn-photo" onClick={handleSelectFromGallery}>
                    <IconImage />
                    <span>Galleria</span>
                  </button>
                </div>

                {/* Lista allegati esistenti */}
                <div className="allegati-list">
                  {getSelectedRapporto()?.allegati?.length === 0 && (
                    <p className="no-allegati">Nessuna foto allegata</p>
                  )}
                  {getSelectedRapporto()?.allegati?.map(allegato => (
                    <div key={allegato.id} className="allegato-item">
                      <img 
                        src={allegato.dataUri} 
                        alt={allegato.descrizione || 'Foto'} 
                        className="allegato-image"
                      />
                      <div className="allegato-info">
                        <span className="allegato-tipo">{allegato.tipoAllegatoNome}</span>
                        {allegato.descrizione && (
                          <span className="allegato-desc">{allegato.descrizione}</span>
                        )}
                      </div>
                      <button 
                        className="btn-icon btn-danger"
                        onClick={() => handleDeleteAllegato(selectedRapportoId, allegato.id)}
                      >
                        <IconTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
